/**
 * Where each subtitle line goes on a frame: pure arithmetic over a text
 * measurer, so node can test it without a canvas.
 *
 * The rules follow libass where it matters for ordinary files:
 *   - font size and vertical distances scale by frameHeight / PlayResY,
 *     horizontal distances by frameWidth / PlayResX;
 *   - outline and shadow widths scale the same way when ScaledBorderAndShadow
 *     is yes, and are frame pixels when it is no;
 *   - alignment is the numpad: 1-3 bottom, 4-6 middle, 7-9 top; 1/4/7 left,
 *     2/5/8 centre, 3/6/9 right. Without \pos the block sits inside the
 *     margins; with \pos the alignment picks which point of the block is
 *     placed on it;
 *   - lines wrap greedily at spaces to the width between the margins (and
 *     between characters for CJK text, which has no spaces). WrapStyle 2
 *     (no wrapping) keeps every line as written;
 *   - cues that would sit on top of each other in the same band and layer are
 *     stacked: at the bottom each later cue goes above the one before, at the
 *     top and middle below it.
 */

import type { Alignment, Point } from '../../../../../contracts/cue-model.ts';
import type { RenderCue, RenderTrack, Rgba, RunStyle } from './model.ts';

/** Width in pixels of `text` drawn in the CSS `font`. */
export type MeasureText = (text: string, font: string) => number;

export interface Frame {
  readonly width: number;
  readonly height: number;
}

/** Line box height as a multiple of the largest font size on the line. */
export const LINE_HEIGHT = 1.2;
/** The baseline sits this far below the line's top, as a multiple of its largest font size. */
export const BASELINE = 0.95;

export interface PlacedRun {
  readonly text: string;
  readonly x: number;
  readonly width: number;
  readonly font: string;
  readonly style: RunStyle;
  /** Frame pixels. */
  readonly size: number;
  readonly outline: number;
  readonly shadow: number;
}

export interface PlacedLine {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly baseline: number;
  readonly runs: readonly PlacedRun[];
}

export interface PlacedCue {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** 0–1, from \fad. */
  readonly alpha: number;
  readonly box: Rgba | null;
  readonly lines: readonly PlacedLine[];
}

const FALLBACK_FAMILIES = 'Arial, Helvetica, "Noto Sans", sans-serif';

/** A CSS font family list naming the style's font first. Quotes are stripped, never escaped. */
export function fontFamily(name: string): string {
  const clean = name.replace(/["'\\;{}]/g, '').trim();
  return clean ? `"${clean}", ${FALLBACK_FAMILIES}` : FALLBACK_FAMILIES;
}

export function cssFont(style: RunStyle, px: number): string {
  const size = Math.max(1, Math.round(px * 100) / 100);
  return `${style.italic ? 'italic ' : ''}${style.bold ? 'bold ' : ''}${size}px ${fontFamily(style.fontName)}`;
}

export function horizontalOf(a: Alignment): 'left' | 'center' | 'right' {
  const col = (a - 1) % 3;
  return col === 0 ? 'left' : col === 1 ? 'center' : 'right';
}

export function verticalOf(a: Alignment): 'bottom' | 'middle' | 'top' {
  return a <= 3 ? 'bottom' : a <= 6 ? 'middle' : 'top';
}

export interface Scale {
  readonly x: number;
  readonly y: number;
  readonly border: number;
}

export function scaleFor(track: RenderTrack, frame: Frame): Scale {
  const y = frame.height / track.playRes.y;
  return { x: frame.width / track.playRes.x, y, border: track.scaleBorder ? y : 1 };
}

interface Token {
  readonly text: string;
  readonly space: boolean;
  readonly style: RunStyle;
  readonly font: string;
  readonly size: number;
  readonly width: number;
}

const CJK = /[\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF\uFF00-\uFFEF]/u;

/** Words, the spaces between them, and one token per CJK character. */
export function tokenize(text: string): { text: string; space: boolean }[] {
  const out: { text: string; space: boolean }[] = [];
  for (const part of text.split(/(\s+)/)) {
    if (part === '') continue;
    if (/^\s+$/.test(part)) {
      out.push({ text: part, space: true });
      continue;
    }
    let word = '';
    for (const ch of part) {
      if (CJK.test(ch)) {
        if (word) out.push({ text: word, space: false });
        word = '';
        out.push({ text: ch, space: false });
      } else {
        word += ch;
      }
    }
    if (word) out.push({ text: word, space: false });
  }
  return out;
}

/**
 * Greedy wrapping of one written line's tokens to `maxWidth`. A token wider
 * than the line on its own gets a line to itself rather than being cut.
 * Spaces never start or end a wrapped line.
 */
export function wrapTokens<T extends { space: boolean; width: number }>(
  tokens: readonly T[],
  maxWidth: number,
): T[][] {
  const lines: T[][] = [];
  let line: T[] = [];
  let width = 0;
  const trimEnd = (l: T[]) => {
    while (l.length > 0 && (l[l.length - 1] as T).space) l.pop();
    return l;
  };
  for (const token of tokens) {
    if (token.space) {
      if (line.length === 0) continue;
      line.push(token);
      width += token.width;
      continue;
    }
    if (line.length > 0 && width + token.width > maxWidth) {
      const done = trimEnd(line);
      if (done.length > 0) lines.push(done);
      line = [];
      width = 0;
    }
    line.push(token);
    width += token.width;
  }
  const last = trimEnd(line);
  if (last.length > 0 || lines.length === 0) lines.push(last);
  return lines;
}

/** Adjacent tokens with one style become one run, so a line is a few fillText calls. */
function mergeRuns(tokens: readonly Token[]): Token[] {
  const out: Token[] = [];
  for (const t of tokens) {
    const prev = out[out.length - 1];
    if (prev && prev.style === t.style) {
      out[out.length - 1] = { ...prev, text: prev.text + t.text, width: prev.width + t.width };
    } else {
      out.push(t);
    }
  }
  return out;
}

/** The point a moving cue is at, `t` seconds into the frame's timeline. */
export function movePoint(cue: RenderCue, t: number): Point | null {
  if (!cue.move) return cue.point;
  const { from, to, t1, t2 } = cue.move;
  const local = t - cue.start;
  const p = local <= t1 ? 0 : local >= t2 ? 1 : (local - t1) / (t2 - t1);
  return { x: from.x + (to.x - from.x) * p, y: from.y + (to.y - from.y) * p };
}

/** \fad's opacity at `t`. */
export function fadeAlpha(cue: RenderCue, t: number): number {
  if (!cue.fade) return 1;
  let alpha = 1;
  const sinceStart = t - cue.start;
  const untilEnd = cue.end - t;
  if (cue.fade.in > 0 && sinceStart < cue.fade.in)
    alpha = Math.min(alpha, sinceStart / cue.fade.in);
  if (cue.fade.out > 0 && untilEnd < cue.fade.out) alpha = Math.min(alpha, untilEnd / cue.fade.out);
  return Math.max(0, Math.min(1, alpha));
}

/** One cue, placed as if nothing else were on screen. */
export function layoutCue(
  cue: RenderCue,
  track: RenderTrack,
  frame: Frame,
  measure: MeasureText,
  t: number = cue.start,
): PlacedCue {
  const scale = scaleFor(track, frame);
  const marginL = cue.margins.left * scale.x;
  const marginR = cue.margins.right * scale.x;
  const marginV = cue.margins.vertical * scale.y;
  const maxWidth = Math.max(1, frame.width - marginL - marginR);
  const h = horizontalOf(cue.alignment);

  const placedLines: PlacedLine[] = [];
  const rows: { tokens: Token[]; width: number; height: number; size: number }[] = [];

  for (const line of cue.lines) {
    const tokens: Token[] = [];
    let lineSize = 0;
    for (const run of line.runs) {
      const size = run.style.fontSize * scale.y;
      lineSize = Math.max(lineSize, size);
      const font = cssFont(run.style, size);
      for (const piece of tokenize(run.text)) {
        tokens.push({
          text: piece.text,
          space: piece.space,
          style: run.style,
          font,
          size,
          width: measure(piece.text, font),
        });
      }
    }
    const wrapped = track.wrap ? wrapTokens(tokens, maxWidth) : [tokens];
    for (const row of wrapped) {
      const size = row.reduce((m, tk) => Math.max(m, tk.size), 0) || lineSize || 1;
      rows.push({
        tokens: mergeRuns(row),
        width: row.reduce((w, tk) => w + tk.width, 0),
        height: size * LINE_HEIGHT,
        size,
      });
    }
  }

  const width = rows.reduce((m, r) => Math.max(m, r.width), 0);
  const height = rows.reduce((s, r) => s + r.height, 0);

  // The block's top-left.
  let x: number;
  let y: number;
  const point = movePoint(cue, t);
  const v = verticalOf(cue.alignment);
  if (point) {
    const ax = point.x * frame.width;
    const ay = point.y * frame.height;
    x = h === 'left' ? ax : h === 'center' ? ax - width / 2 : ax - width;
    y = v === 'top' ? ay : v === 'middle' ? ay - height / 2 : ay - height;
  } else {
    x =
      h === 'left'
        ? marginL
        : h === 'right'
          ? frame.width - marginR - width
          : marginL + (frame.width - marginL - marginR - width) / 2;
    y =
      v === 'top'
        ? marginV
        : v === 'middle'
          ? (frame.height - height) / 2
          : frame.height - marginV - height;
  }

  let top = y;
  for (const row of rows) {
    const lx =
      h === 'left' ? x : h === 'center' ? x + (width - row.width) / 2 : x + width - row.width;
    let cursor = lx;
    const runs: PlacedRun[] = row.tokens.map((tk) => {
      const placed: PlacedRun = {
        text: tk.text,
        x: cursor,
        width: tk.width,
        font: tk.font,
        style: tk.style,
        size: tk.size,
        outline: tk.style.outlineWidth * scale.border,
        shadow: tk.style.shadowDepth * scale.border,
      };
      cursor += tk.width;
      return placed;
    });
    placedLines.push({
      x: lx,
      y: top,
      width: row.width,
      height: row.height,
      baseline: top + row.size * BASELINE,
      runs,
    });
    top += row.height;
  }

  return { x, y, width, height, alpha: fadeAlpha(cue, t), box: cue.box, lines: placedLines };
}

function shifted(p: PlacedCue, dy: number): PlacedCue {
  if (dy === 0) return p;
  return {
    ...p,
    y: p.y + dy,
    lines: p.lines.map((l) => ({ ...l, y: l.y + dy, baseline: l.baseline + dy })),
  };
}

/**
 * Every cue showing at `t`, placed, with collisions stacked. `cues` is in draw
 * order (layer, then file order), as `activeCues` returns it.
 */
export function layoutFrame(
  cues: readonly RenderCue[],
  track: RenderTrack,
  frame: Frame,
  measure: MeasureText,
  t: number,
): PlacedCue[] {
  const used = new Map<string, number>();
  return cues.map((cue) => {
    const placed = layoutCue(cue, track, frame, measure, t);
    if (cue.point || cue.move) return placed;
    const band = verticalOf(cue.alignment);
    const key = `${cue.layer}:${band}`;
    const offset = used.get(key) ?? 0;
    used.set(key, offset + placed.height);
    return shifted(placed, band === 'bottom' ? -offset : offset);
  });
}
